
import React from 'react';
import _ from 'lodash';
import 'moment';

/**
 * Assign a vertical row offset to each item so that, within a single
 * calendar day, items read top-to-bottom in start-time order, and the
 * stacking resets at the top again at the start of each new day.
 * Overlapping items (including an overnight item's tail) are never placed
 * on the same line.
 * @external {moment} http://momentjs.com/
 * @param  {Object[]} items List of items, each with moment `start`/`end`
 * @returns {Object[]} Clones of the items, each with a `rowOffset` number
 */
export function assignRowOffsets(items) {
  const sortedItems = _.sortBy(items, i => i.start.unix());
  const rowEnds = []; // (rowOffset) => end moment of the last item on that row
  let currentDay = null;
  let nextRow = 0;
  return _.map(sortedItems, (original) => {
    const item = _.clone(original);
    const day = item.start.format('YYYY-MM-DD');
    if (day !== currentDay) {
      currentDay = day;
      nextRow = 0; // new day: start stacking from the top again
    }
    let row = nextRow;
    while (rowEnds[row] !== undefined && rowEnds[row].isAfter(item.start)) {
      row += 1; // skip rows still occupied by an earlier (overnight) item
    }
    item.rowOffset = row;
    rowEnds[row] = item.end;
    nextRow = row + 1; // keep later same-day items strictly below earlier ones
    return item;
  });
}

/**
 * Original greedy bin-packing offset assignment: pack items onto as few rows
 * as possible, filling each row left-to-right with non-overlapping items.
 * An item's row is NOT tied to its start time. This is the default layout for
 * ordinary user rows; the start-time layout (assignRowOffsets) is reserved for
 * unallocated rows (see shouldUseStartTimeOrdering).
 * @param  {Object[]} items List of items, each with moment `start`/`end`
 * @returns {Object[]} Clones of the items, each with a `rowOffset` number
 */
export function greedyRowOffsets(items) {
  // sorted in reverse order as we iterate over the array backwards
  const remaining = _.sortBy(items, i => -i.start.unix());
  const displayItems = [];
  let rowOffset = 0;
  while (remaining.length > 0) {
    let lastEnd = null;
    for (let i = remaining.length - 1; i >= 0; i--) {
      if (lastEnd === null || remaining[i].start >= lastEnd) {
        const item = _.clone(remaining[i]);
        item.rowOffset = rowOffset;
        displayItems.push(item);
        remaining.splice(i, 1);
        lastEnd = item.end;
      }
    }
    rowOffset++;
  }
  return displayItems;
}

/**
 * Decide whether a row should use the start-time stacking layout
 * (assignRowOffsets) instead of the default greedy layout. True only for
 * unallocated rows: the dedicated Unallocated row (user_id === -1), OR a real
 * user whose first name, last name, or email contains "unallocated"
 * (case-insensitive). All items in a row share one user, so the first item
 * decides the whole row.
 * @param  {Object[]} items List of items for a single row
 * @returns {boolean} True if the row should stack by start time
 */
export function shouldUseStartTimeOrdering(items) {
  const first = items[0];
  if (!first) return false;
  if (first.user_id === -1) return true;
  const user = first.user || {};
  const haystack = `${user.first_name || ''} ${user.last_name || ''} ${user.email || ''}`.toLowerCase();
  return haystack.includes('unallocated');
}

/**
 * Assign row offsets for a single row, choosing the layout based on the row's
 * user: start-time stacking for unallocated rows, greedy packing otherwise.
 * @param  {Object[]} items List of items for a single row
 * @returns {Object[]} Clones of the items, each with a `rowOffset` number
 */
export function assignRowOffsetsForRow(items) {
  return shouldUseStartTimeOrdering(items) ? assignRowOffsets(items) : greedyRowOffsets(items);
}

/**
 * Render all items in a row
 * @external {moment} http://momentjs.com/
 * @param  {Object[]} items List of items to render for this row
 * @param  {moment} vis_start The visible start of the timeline
 * @param  {moment} vis_end The visible end of the timeline
 * @param  {number} total_width pixel width of the timeline
 */
export function rowItemsRenderer(items, vis_start, vis_end, total_width, itemHeight, itemRenderer, selectedItems = []) {
  const start_end_min = vis_end.diff(vis_start, 'minutes');
  const pixels_per_min = total_width / start_end_min;
  const filtered_items = _.filter(
    items,
    i => !i.end.isBefore(vis_start) && !i.start.isAfter(vis_end) // if end not before window && start not after window
  );
  const displayItems = assignRowOffsetsForRow(filtered_items);
  return _.map(displayItems, (i) => {
    const { color } = i;
    const Comp = itemRenderer;
    const top = itemHeight * i.rowOffset;
    const item_offset_mins = i.start.diff(vis_start, 'minutes');
    const item_duration_mins = i.end.diff(i.start, 'minutes');
    const left = Math.round(item_offset_mins * pixels_per_min);
    const width = Math.round(item_duration_mins * pixels_per_min);
    let compClassnames = 'rct9k-items-inner';
    let outerClassnames = 'rct9k-items-outer item_draggable';
    let style = { backgroundColor: color };
    const isSelected = selectedItems.indexOf(Number(i.key)) > -1;

    if (isSelected) {
      compClassnames += ' rct9k-items-selected';
      outerClassnames += ' rct9k-items-outer-selected';
      style = {};
    }

    return (
      <span
        key={i.key}
        data-item-index={i.key}
        className={outerClassnames}
        style={{
          left, width, top, backgroundColor: 'transparent'
        }}
      >
        <Comp key={i.key} item={i} className={compClassnames} style={style} />
      </span>
    );
  });
}

/**
 * Render row layers
 * @param  {Object[]} layers List of layers to render for this row
 * @param  {moment} vis_start The visible start of the timeline
 * @param  {moment} vis_end The visible end of the timeline
 * @param  {number} total_width pixel width of the timeline
 * @param  {number} itemHeight The layer height in px
 */
export function rowLayerRenderer(layers, vis_start, vis_end, total_width, itemHeight) {
  const start_end_min = vis_end.diff(vis_start, 'minutes');
  const pixels_per_min = total_width / start_end_min;
  const filtered_items = _.sortBy(
    _.filter(layers, i => !i.end.isBefore(vis_start) && !i.start.isAfter(vis_end)),
    i => -i.start.unix()
  ); // sorted in reverse order as we iterate over the array backwards
  const displayItems = [];
  let rowOffset = 0;
  while (filtered_items.length > 0) {
    let lastEnd = null;
    for (let i = filtered_items.length - 1; i >= 0; i--) {
      if (lastEnd === null || filtered_items[i].start >= lastEnd) {
        const item = _.clone(filtered_items[i]);
        item.rowOffset = rowOffset;
        displayItems.push(item);
        filtered_items.splice(i, 1);
        lastEnd = item.end;
      }
    }
    rowOffset++;
  }
  return _.map(displayItems, (i) => {
    const { style, rowNumber } = i;
    const top = itemHeight * i.rowOffset;
    const item_offset_mins = i.start.diff(vis_start, 'minutes');
    const item_duration_mins = i.end.diff(i.start, 'minutes');
    const left = Math.round(item_offset_mins * pixels_per_min);
    const width = Math.round(item_duration_mins * pixels_per_min);
    const height = itemHeight - (rowNumber === 0 ? 2 : 1); // for border
    const outerClassnames = 'rct9k-row-layer';

    return (
      <div
        key={`r-${rowNumber}-${i.start.unix()}`}
        data-item-index={i.key}
        className={outerClassnames}
        style={{
          ...style, left, width, top, height
        }}
      />
    );
  });
}

/**
 * Gets the row object for a given x and y pixel location
 * @param  {number} x The x coordinate of the pixel location
 * @param  {number} y The y coordinate of the pixel location
 * @param  {Object} topDiv Div to search under
 * @returns {Object} The row object at that coordinate
 */
export function getNearestRowObject(x, y, topDiv = document) {
  const elementsAtPixel = document.elementsFromPoint(x, y);
  return _.find(elementsAtPixel, (e) => {
    const inDiv = topDiv.contains(e);
    return inDiv && e.hasAttribute('data-row-index');
  });
}

/**
 * Gets the row number for a given row object
 * @param  {Object} elem The row object
 * @returns {number} The row number
 */
export function getRowObjectRowNumber(elem) {
  return Number(elem ? elem.getAttribute('data-row-index') : 0);
}

/**
 * Gets the vertical margins and borders given an object
 * @param  {Object} elem The row object
 * @returns {number} the pixel position of the bottom of the element
 */
export function getVerticalMarginBorder(elem) {
  const computedStyles = window.getComputedStyle(elem);
  // top margin plus bottom margin halved
  const rowMargins = (Math.ceil(parseFloat(computedStyles.marginTop) + parseFloat(computedStyles.marginBottom)) || 1) / 2;
  // half the size of the border seems important
  const rowBorders = (Math.ceil(parseFloat(computedStyles.borderTopWidth) + parseFloat(computedStyles.borderBottomWidth)) || 1)
    / 2;
  return Number(rowMargins + rowBorders);
}

/**
 * Gets the true bottom location given an object
 * @param  {Object} elem an element
 * @returns {number} the pixel position of the bottom of the element
 */
export function getTrueBottom(elem) {
  /*
  @bendog: leaving this here as a helper, if there's ever a bug around inner items size
  // get object shape
  const rects = elem.getClientRects();
  const bottom = Math.max(Object.values(rects).map(o => o.bottom), 0);
   */
  // calculate the true bottom
  const bound = elem.getBoundingClientRect();
  const bottom = Math.floor(bound.top + bound.height);
  return Number(bottom);
}

/**
 * Gets the row number for a given x and y pixel location
 * @param  {number} x The x coordinate of the pixel location
 * @param  {number} y The y coordinate of the pixel location
 * @param  {Object} topDiv Div to search under
 * @returns {number} The row number
 */
export function getNearestRowNumber(x, y, topDiv = document) {
  const elementsAtPixel = document.elementsFromPoint(x, y);
  const targetRow = _.find(elementsAtPixel, (e) => {
    const inDiv = topDiv.contains(e);
    return inDiv && e.hasAttribute('data-row-index');
  });
  return targetRow ? targetRow.getAttribute('data-row-index') : 0;
}

/**
 * Use to find the height of a row, given a set of items
 * @param  {Object[]} items List of items
 * @returns {number} Max row height
 */
export function getMaxOverlappingItems(items) {
  const placedItems = assignRowOffsetsForRow(items);
  const maxOffset = _.reduce(placedItems, (max, i) => Math.max(max, i.rowOffset), 0);
  return Math.max(maxOffset + 1, 1);
}
